import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { IBusinessUpdateInfoPayload, IRatingAppProps, ITokenEcwid, UpdateMetaPayloadProps } from '../../..';
import {
  OnboardingBusinessConfigGmbStatusDataProps,
  OnboardingBusinessConfigNotificationsDataProps,
  OnboardingBusinessConfigPaymentsDataProps,
  OnboardingBusinessConfigScheduleDataProps,
  OnboardingBusinessConfigServicesDataProps,
  OnboardingBusinessConfigSocialNetworksDataProps,
  OnboardingBusinessConfigWebsiteDataProps,
} from '../../models/BusinessConfig.model';
import {
  BusinessSiteFields,
  BusinessUpdatePayloadProps,
  IDeleteImageFormProps,
  IGetSiteImagesProps,
} from '../../repositories/IOnboarding.api';
import * as fromBusinessReducer from '../business-config/business-config.reducer';
import * as fromOnboardingActions from '../onboarding.actions';
import * as fromSelectors from './business-config.selectors';

@Injectable()
export class OnboardingBusinessConfigStore {
  constructor(
    public store: Store<fromBusinessReducer.IBusinessDataAndGmbStatusState>
  ) {}

  //// BUSINESS & GMB-Status STATE
  get state$() {
    return this.store.select(fromSelectors.getBusinessAndGmbStatusState);
  }

  //// BUSINESS STATE
  get businessState$() {
    return this.store.select(fromSelectors.getBusinessState);
  }
  get businessData$() {
    return this.store.select(fromSelectors.getBusinessStateData);
  }
  get businessIsLoading$() {
    return this.store.select(fromSelectors.getBusinessStateIsLoading);
  }
  get businessSuccess$() {
    return this.store.select(fromSelectors.getBusinessStateSuccess);
  }
  get businessError$() {
    return this.store.select(fromSelectors.getBusinessStateError);
  }
  get fetched$() {
    return this.store.select(fromSelectors.getBusinessHasBeenFetched);
  }

  //// GMB-STATUS STATE
  get gmbStatusState$() {
    return this.store.select(fromSelectors.getGmbStatusState);
  }
  get gmbStatusData$() {
    return this.store.select(fromSelectors.getGmbStatusStateData);
  }
  get gmbStatusIsLoading$() {
    return this.store.select(fromSelectors.getGmbStatusStateIsLoading);
  }
  get gmbStatusSuccess$() {
    return this.store.select(fromSelectors.getGmbStatusStateSuccess);
  }
  get gmbStatusError$() {
    return this.store.select(fromSelectors.getGmbStatusStateError);
  }
  get gmbStatusHasBeenFetched$() {
    return this.store.select(fromSelectors.gmbStatusHasBeenFetched);
  }

  getGmbLocationStatus = () => {
    this.store.dispatch(fromOnboardingActions.getLocationStatusBeginAction());
  };

  // ------------- ONBOARDING MODULES
  get modulesCompletenes$() {
    return this.store.select(fromSelectors.getBusinessAdditionalInfoState);
  }

  setServicesCompleted(payload: OnboardingBusinessConfigServicesDataProps) {
    return this.store.dispatch(
      fromOnboardingActions.setServicesCompletedAction({ payload })
    );
  }
  setScheduleCompleted(payload: OnboardingBusinessConfigScheduleDataProps) {
    return this.store.dispatch(
      fromOnboardingActions.setScheduleCompletedAction({ payload })
    );
  }
  setPaymentMethodsComplete(
    payload: OnboardingBusinessConfigPaymentsDataProps
  ) {
    return this.store.dispatch(
      fromOnboardingActions.setPaymentMethodsCompletedAction({ payload })
    );
  }
  setSocialNetworksCompleted(
    payload: OnboardingBusinessConfigSocialNetworksDataProps
  ) {
    return this.store.dispatch(
      fromOnboardingActions.setSocialNetworksCompletedAction({ payload })
    );
  }
  setGmbStatusCompleted(payload: OnboardingBusinessConfigGmbStatusDataProps) {
    return this.store.dispatch(
      fromOnboardingActions.setGmbStatusCompletedAction({ payload })
    );
  }
  setNotificationsCompleted(
    payload: OnboardingBusinessConfigNotificationsDataProps
  ) {
    return this.store.dispatch(
      fromOnboardingActions.setNotificationsCompletedAction({ payload })
    );
  }
  setWebsiteCompleted(payload: OnboardingBusinessConfigWebsiteDataProps) {
    return this.store.dispatch(
      fromOnboardingActions.setWebsiteCompletedAction({ payload })
    );
  }
  setModuleCompleted() {
    return this.store.dispatch(
      fromOnboardingActions.setModuleCompletedAction()
    );
  }

  getBusinesData = () => {
    this.store.dispatch(fromOnboardingActions.getBusinessDataBeginAction());
  };

  updateBusinessData = (payload: BusinessUpdatePayloadProps) => {
    this.store.dispatch(
      fromOnboardingActions.updateBusinessDataBeginAction({ payload })
    );
  };
  updateBusinessInfo = (payload: IBusinessUpdateInfoPayload) => {
    this.store.dispatch(
      fromOnboardingActions.updateBusinessInfoBeginAction({ payload })
    );
  };

  updateMeta = (payload: UpdateMetaPayloadProps) => {
    this.store.dispatch(
      fromOnboardingActions.updateMetaBeginAction({ payload })
    );
  };

  updateImageWebSiteBusinessData = (payload: string) => {
    this.store.dispatch(
      fromOnboardingActions.updateWebsiteImageSuccessAction({ payload })
    );
  };

  removeLogoSiteBusinessData = (payload: string) => {
    this.store.dispatch(
      fromOnboardingActions.removeLogoSite({ payload })
    );
  };

  removeAboutSiteBusinessData = (payload: string) => {
    this.store.dispatch(
      fromOnboardingActions.removeBannerSite({ payload })
    );
  };

  removeMainImageSiteBusinessData = (payload: string) => {
    this.store.dispatch(
      fromOnboardingActions.removeMainImageSite({ payload })
    );
  };

  removeContactImageSiteBusinessData = (payload: string) => {
    this.store.dispatch(
      fromOnboardingActions.removeContactImageSite({ payload })
    );
  };

  removeSpotlightImageSiteBusinessData = (payload: string) => {
    this.store.dispatch(
      fromOnboardingActions.removeSpotlightImageSite({ payload })
    );
  };

  removeGallerySiteBusinessData = (payload: any[]) => {
    this.store.dispatch(
      fromOnboardingActions.removeGalleryImageSite({ payload })
    );
  };

  updateFieldsSite = (payload: BusinessSiteFields) => {
    this.store.dispatch(
      fromOnboardingActions.updateFieldsSite({ payload })
    );
  };

  updateImagesSite = (payload: any) => {
    this.store.dispatch(
      fromOnboardingActions.updateImagesSite({ payload })
    );
  };


  //// SERVICES
  prefillServicesData = (payload: OnboardingBusinessConfigServicesDataProps) =>
    this.store.dispatch(
      fromOnboardingActions.prefillServicesDataAction({ payload })
    );

  //// SCHEDULE
  prefillScheduleData = (payload: OnboardingBusinessConfigScheduleDataProps) =>
    this.store.dispatch(
      fromOnboardingActions.prefillScheduleDataAction({ payload })
    );

  //// GMB STATUS
  getGmbStatus = () => {
    this.store.dispatch(fromOnboardingActions.getGmbStatusBeginAction());
  };

  //// WEBSITE
  createWebsite = () => {
    this.store.dispatch(fromOnboardingActions.createWebsiteBeginAction());
  };

  //// BUSINESS IMAGES
  deleteBusinessImage = (payload: IDeleteImageFormProps) => {
    this.store.dispatch(
      fromOnboardingActions.deleteBusinessImageBeginAction({ payload })
    );
  };

  //// STORE
  updateStoreData = (payload: ITokenEcwid) => {
    this.store.dispatch(
      fromOnboardingActions.updateStoreInfoBeginAction({ payload })
    );
  };

  saveScoreData = (payload: IRatingAppProps) => {
    this.store.dispatch(
      fromOnboardingActions.updateScoreInfoBeginAction({ payload })
    );
  };

}
